import React, { useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { WindowManagerProvider, useWindowManager } from "../../lib/windowManager/WindowManagerContext";
import {
  SECTION_IDS,
  findSectionForPath,
  getVisibleSections,
  defaultTitleForPath,
} from "../../lib/windowManager/registry";
import { useDesktopMode } from "../../lib/windowManager/useDesktopMode";
import WindowFrame from "./WindowFrame";
import DesktopIcon from "./DesktopIcon";
import Taskbar from "./Taskbar";
import MobileChrome from "./MobileChrome";
import BootScreen from "./BootScreen";
import QuickAddFab from "../QuickAddFab";

export function DesktopShell() {
  const location = useLocation();
  const initialActive = findSectionForPath(location.pathname).id;
  return (
    <WindowManagerProvider windowIds={SECTION_IDS} openByDefault={[initialActive]}>
      <DesktopShellInner />
    </WindowManagerProvider>
  );
}

function DesktopShellInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isDesktop = useDesktopMode();
  const { windows, openWindow, focusWindow, setRects } = useWindowManager();
  const layerRef = useRef(null);
  const sizedRef = useRef(new Set());

  const pathname = location.pathname;
  const activeSection = findSectionForPath(pathname);
  const activeId = activeSection.id;
  const sections = getVisibleSections(isAdmin);

  // Scope the teal OS-desktop background to shell pages only, so /login,
  // /register and /pay/:id (which never mount the shell) keep their own look.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("win98-shell-active");
    return () => root.classList.remove("win98-shell-active");
  }, []);

  // Whichever section the URL points at must be open/focused — this makes hard
  // reloads and deep links (e.g. /invoices/:id/edit) boot straight into the right
  // window. Also fills the window to the working area the first time it opens.
  useEffect(() => {
    openWindow(activeId);
    if (!isDesktop) return;
    if (sizedRef.current.has(activeId)) return;
    const layer = layerRef.current;
    if (!layer) return;
    const w = layer.clientWidth;
    const h = layer.clientHeight;
    if (w <= 0 || h <= 0) return;
    sizedRef.current.add(activeId);
    setRects({
      [activeId]: { x: 6, y: 6, width: Math.max(320, w - 12), height: Math.max(240, h - 12) },
    });
  }, [pathname, isDesktop, activeId, openWindow, setRects]);

  function activate(entry) {
    if (entry.route !== pathname) {
      navigate(entry.route);
      return;
    }
    const w = windows[entry.id];
    if (!w) return;
    if (w.status === "open") focusWindow(entry.id);
    else openWindow(entry.id);
  }

  const title = windows[activeId]?.title || defaultTitleForPath(pathname);

  if (!isDesktop) {
    // Mobile: a fixed full-viewport flex column. Content scrolls in an inner
    // container so the root document never scrolls — that's what keeps the
    // bottom bar reliably visible (no dynamic-toolbar/containing-block quirks).
    return (
      <>
        <BootScreen />
        <div className="win98-mobile-shell">
          <div className="win98-mobile-scroll">
            <div className="win98-desktop" data-mode="stacked">
              <main className="win98-stacked-layer">
                <WindowFrame key={activeId} id={activeId} title={title} isDesktop={false}>
                  <Outlet />
                </WindowFrame>
              </main>
            </div>
          </div>
          <MobileChrome />
        </div>
        <QuickAddFab />
      </>
    );
  }

  return (
    <>
      <BootScreen />
      <div className="win98-desktop" data-mode="desktop">
        <nav className="win98-icon-grid" aria-label="Desktop icons">
          {sections.map((entry) => (
            <DesktopIcon key={entry.id} entry={entry} onActivate={() => activate(entry)} />
          ))}
        </nav>
        <main ref={layerRef} className="win98-window-layer">
          <WindowFrame key={activeId} id={activeId} title={title} isDesktop={true}>
            <Outlet />
          </WindowFrame>
        </main>
      </div>
      <div className="win-well win98-footer-strip">
        <p>© KyberBusiness</p>
      </div>
      <Taskbar />
    </>
  );
}
